// API utility with token handling
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

// Ensure API_BASE_URL ends with a slash
const normalizedApiUrl = API_BASE_URL?.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  'api/webuser/login',
  'api/webuser/register',
  'api/webuser/forgot-password',
  'api/webuser/reset-password'
];

/**
 * Custom fetch wrapper that handles authentication errors
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise} - API response
 */
export const fetchWithAuth = async (endpoint, options = {}) => {
  const token = localStorage.getItem('userToken_TOVIAPPS');
  
  // Check if this is a public endpoint
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some(publicEndpoint => 
    endpoint.includes(publicEndpoint)
  );
  
  // Set up headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add auth token only if available and not a public endpoint
  if (token && !isPublicEndpoint) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(`${normalizedApiUrl}${endpoint}`, {
      ...options,
      headers,
    });
    
    // Handle 401 Unauthorized (expired token)
    if (response.status === 401) {
      console.error('Authentication failed - token expired or invalid');
      
      // Don't redirect for public endpoints (they might already be on login page)
      if (!isPublicEndpoint) {
        // Clear token and redirect to login
        localStorage.removeItem('userToken_TOVIAPPS');
        window.location.href = '/login';
        return null;
      }
      
      // For public endpoints, just return the response to let the component handle it
      return response;
    }
    
    // Handle 403 Forbidden (insufficient permissions)
    if (response.status === 403) {
      console.error('Forbidden - insufficient permissions');
      // Redirect to main page
      window.location.href = '/main';
      return null;
    }
    
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    // Don't throw for public endpoints - let the component handle it
    if (isPublicEndpoint) {
      return null;
    }
    throw error;
  }
};

/**
 * API methods with authentication handling
 */
export const api = {
  get: (endpoint) => fetchWithAuth(endpoint, { method: 'GET' }),
  post: (endpoint, data) => fetchWithAuth(endpoint, { 
    method: 'POST',
    body: JSON.stringify(data)
  }),
  put: (endpoint, data) => fetchWithAuth(endpoint, { 
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (endpoint) => fetchWithAuth(endpoint, { method: 'DELETE' }),
};

export default api;
